import React, { useState, useEffect } from 'react';
import SEOHead from '../components/SEOHead';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Shield, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const PrivacyPolicyPage = () => {
  const [loading, setLoading] = useState(true);
  const [pageData, setPageData] = useState({
    title: 'Privacy Policy',
    last_updated: 'February 2026',
    sections: []
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(`${API_URL}/api/cms/legal/privacy`);
        if (res.ok) {
          const data = await res.json();
          if (data && data.sections?.length > 0) {
            setPageData(data);
          }
        }
      } catch (error) {
        console.error('Failed to fetch privacy policy:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Default sections if CMS is empty
  const defaultSections = [
    {
      title: '1. Introduction',
      content: `GetCare 360 Pty Ltd ("GetCare 360", "we", "us", or "our") is committed to protecting the privacy of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our NDIS provider management platform and related services.\n\nWe comply with the Australian Privacy Principles (APPs) contained in the Privacy Act 1988 (Cth) and the NDIS Quality and Safeguards Commission requirements for handling sensitive information.`
    },
    {
      title: '2. Information We Collect',
      content: `We collect the following types of information:\n\n• Personal Information: Name, email address, phone number, business address, organisation details (ABN, NDIS registration number), billing and payment information, login credentials.\n\n• NDIS Participant Information: Participant names, contact details, NDIS numbers, support plans, goals, service agreements, health information, incident reports, progress notes, and funding information.\n\n• Technical Information: IP addresses, device information, browser type, operating system, usage data, analytics, and cookies.`
    },
    {
      title: '3. How We Use Your Information',
      content: `We use collected information to:\n\n• Provide, maintain, and improve our NDIS management platform\n• Process NDIS claims and generate compliance reports\n• Facilitate communication between providers, participants, and support workers\n• Send important service updates and notifications\n• Provide customer support and respond to inquiries\n• Ensure platform security and prevent fraud\n• Comply with legal obligations and NDIS requirements\n• Generate anonymised analytics to improve our services`
    },
    {
      title: '4. Data Security',
      content: `We implement industry-standard security measures to protect your data:\n\n• 256-bit SSL/TLS encryption for all data transmission\n• AES-256 encryption for data at rest\n• Australian-based cloud servers (AWS Sydney region)\n• Regular security audits and penetration testing\n• Multi-factor authentication options\n• Role-based access controls\n• Automated backup systems`
    },
    {
      title: '5. Your Rights',
      content: `Under the Privacy Act 1988 and APPs, you have the right to:\n\n• Access personal information we hold about you\n• Request correction of inaccurate information\n• Request deletion of your data (subject to legal requirements)\n• Withdraw consent for marketing communications\n• Lodge a complaint with the OAIC if you believe we've breached your privacy`
    },
    {
      title: '6. Contact Us',
      content: `For privacy-related inquiries or to exercise your rights, please contact our Privacy Officer:\n\nEmail: privacy@getcare360.com.au\nPhone: 1800 GET CARE\nAddress: Australia Wide\n\nWe will respond to your request within 30 days.`
    }
  ];

  const sections = pageData.sections?.length > 0 ? pageData.sections : defaultSections;

  return (
    <div className="min-h-screen bg-page-bg">
      <SEOHead 
        title="Privacy Policy | GetCare 360 - Data Protection & Privacy"
        description="Read GetCare 360's Privacy Policy. Learn how we protect your data and comply with Australian Privacy Principles and NDIS requirements."
        keywords="GetCare 360 privacy policy, NDIS data protection, Australian privacy compliance, disability care data security"
        canonicalUrl="/privacy-policy"
        page="privacy-policy"
        noIndex={false}
      />
      <Header />
      
      {/* Hero */}
      <section className="bg-gradient-to-br from-primary-navy via-primary-navy to-tech-blue pt-32 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Shield className="text-white" size={32} />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">{pageData.title}</h1>
          <p className="text-white/70">Last updated: {pageData.last_updated}</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="animate-spin h-8 w-8 text-tech-blue" />
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 space-y-8">
              {sections.map((section, index) => (
                <div key={index}>
                  <h2 className="text-2xl font-bold text-primary-navy mb-4">{section.title}</h2>
                  <div className="text-gray-600 leading-relaxed whitespace-pre-line">
                    {section.content}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;
